/**
 * The profile overlay's lazy entry point and the start screen's settings owner
 * (profile-overlay-and-menu-slim design § Load path, § Settings ownership).
 *
 * A deliberate leaf: it imports no sibling in this package, so both in-package callers — the name
 * slot in `game-start-ui.js` and the Multiplayer section's unregistered nudge — can reach it
 * without a cycle (the section is itself imported by `game-start-ui.js`, so routing the nudge
 * through that file would close one). `game-start-ui.js` re-exports `openProfileOverlay` for
 * `game-menu.js`, keeping the package's single-entry-point boundary intact.
 *
 * The overlay itself is fetched at open time (the menu-button precedent), keeping it out of the
 * eager start-screen boot graph — [[docs/PERFORMANCE]] "keep the load path flat".
 */

/**
 * The start screen's ONE live settings object — the same one `renderGameStart` threads into the
 * name slot, the mode cards, the control bank and Play's session create. Registered here (the
 * `nameSlotRenderState` precedent, js/CLAUDE.md § Module-level state exceptions) so that every
 * opener gets the real object rather than a fresh `{playerName: …}` snapshot: an anonymous rename
 * through the Menu or the Multiplayer nudge used to mutate a throwaway and leave the render-held
 * object holding the old name, which is the name Play then sent.
 */
let startSettings = null;

export function registerStartSettings(settings) {
  startSettings = settings;
}

/**
 * Opens the profile overlay. Callers supply their context — `screen` decides which settings object
 * the overlay writes through and which behaviour table its sections follow, `source` is the
 * `profile-opened` analytics dimension answering the initiative's discoverability question.
 *
 * In-game there is no start screen to desync, and the next `renderGameStart` rebuilds from
 * localStorage anyway, so a plain snapshot is the honest object there.
 */
export function openProfileOverlay(rerenderSlot, { screen, source }) {
  const settings =
    screen === "start"
      ? startSettings
      : { playerName: localStorage.getItem("playerName") || "" };
  return import("./game-start-profile-overlay.js").then((m) =>
    m.mountProfileOverlay(settings, rerenderSlot, { screen, source }),
  );
}
